import { fg } from '@atlaskit/platform-feature-flags/fg';

/**
 * Capability tokens that the backend recognises via the `X-Rovo-Chat-Capabilities`
 * header. Each capability opts the request into a piece of backend behaviour.
 */
export const STORE_MESSAGE_FROM_USER_SETTINGS_CAPABILITY = 'store-message-from-user-settings';
/** Inline actionable follow-ups: questions are woven into prose. */
export const ACTIONABLE_FOLLOW_UPS_CAPABILITY = 'actionable-follow-ups';
/** Stacked actionable follow-ups: questions returned as a plain list, no prose. */
export const ACTIONABLE_FOLLOW_UPS_STACKED_CAPABILITY = 'actionable-follow-ups-stacked';

/**
 * Maps each gated capability to the gate that enables it. Add new
 * capability/gate pairs here rather than introducing nested ternaries at the
 * call sites.
 */
const GATED_CAPABILITIES: ReadonlyArray<{ capability: string; isEnabled: () => boolean }> = [
	{
		capability: STORE_MESSAGE_FROM_USER_SETTINGS_CAPABILITY,
		isEnabled: () => fg('ai_mate_store_messages_be_driven'),
	},
];

/**
 * Adds gate-enabled service capabilities to the caller's capabilities. Follow-up
 * formats must be advertised by the UI that can render them; upgrading the service
 * alone must not opt an older UI into a new response format.
 */
export const buildEffectiveCapabilities = (capabilities?: string[]): string[] | undefined => {
	const enabledCapabilities = GATED_CAPABILITIES.filter(({ isEnabled }) => isEnabled()).map(
		({ capability }) => capability,
	);

	// Preserve older UIs' inline opt-in, but never request both formats.
	const callerCapabilities = capabilities?.includes(ACTIONABLE_FOLLOW_UPS_STACKED_CAPABILITY)
		? capabilities.filter((capability) => capability !== ACTIONABLE_FOLLOW_UPS_CAPABILITY)
		: capabilities;

	if (callerCapabilities === undefined && enabledCapabilities.length === 0) {
		return undefined;
	}

	return Array.from(new Set([...(callerCapabilities ?? []), ...enabledCapabilities]));
};
